/* istanbul ignore file */
import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { connect, useDispatch, useSelector } from 'react-redux'

import ParseEntry from 'utils/contentfulParser'
import { CurriculumRouteContentGetter } from 'layers/content/v3/Hocs/CurriculumRouteContentGetter'
import withSiteContext from 'layers/content/v2/Hocs/withSiteContext'
import withApiError from 'layers/errorHandling/apiError'
import {
  resolveRouteServiceApp,
  showSiteSelector,
  isHighSchoolUrl,
  isK5Url,
  isMiddleSchoolUrl,
  isPreviewRoute,
} from 'utils/routing/routeHelpers'
import {
  isPreviewMode,
  transformInstructorDataToOldShape,
} from 'utils/highschoolHelpers'
import {
  operations as lmsOperations,
  selectors as lmsSelectors,
} from 'store/lmsManager'
import {
  operations as curriculumCourseOperations,
  selectors as curriculumCourseSelectors,
} from 'store/curriculumCourseManager'

import ResourcesContentV3 from '../component'
import { determinePageLayout } from '../pageLayouts'
import {
  buildCoursePointerForTreeFetch,
  getInstructorDataForCurrentProgram,
  parseCoursePointerQuery,
  resolveCourseNodeId,
  resolveTrainingCourseCodeForTree,
  resolveTrainingProgramConfig,
  resolveTrainingProgress,
} from 'utils/program/training'

const mapArticleEntry = entry => {
  if (!entry) return null

  const { breadcrumb, markComplete } = entry || {}
  const { derivedContent, rootContent, content } = ParseEntry(
    entry,
    isPreviewMode(),
  )
  const { anchorNavigation } = derivedContent || {}
  const { landingBanner } = rootContent || {}
  const pageLayout = determinePageLayout(anchorNavigation)
  const currentLocale = entry?.locale

  return {
    anchorNavigation,
    breadcrumb,
    content,
    landingBanner,
    markComplete,
    pageLayout,
    currentLocale,
  }
}

export const TrainingArticleV3Container = props => {
  const dispatch = useDispatch()
  const {
    currentSite,
    highSchoolInstructorData,
    instructorData,
    productTabTitle,
    routeContent,
    useCurriculumLmsPayload,
    userContextManager,
  } = props || {}

  const { content = {}, route = {}, changeLocale, locale } = routeContent || {}
  const queryType = route?.queryType
  const article =
    (queryType && content?.[queryType]) || content?.article || null
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  const isHighSchool = isHighSchoolUrl()

  // Two sources of program identity — they should agree in normal operation:
  // - routeContent.contentGroupCode: from curriculum-service; drives Redux keys
  //   and the course-tree fetch.
  // - programConfig (from URL pathname): drives instructorDataKey and training
  //   course-code resolution for K5/MS/HS article trainings.
  // routeContent wins for contentGroupCode when present; URL config is the
  // fallback when route content has not yet supplied a group code.
  const programConfig = resolveTrainingProgramConfig(pathname)
  const routeContentGroupCode = routeContent?.contentGroupCode || null
  const contentGroupCode =
    routeContentGroupCode || programConfig?.contentGroupCode || null

  useEffect(() => {
    if (
      process.env.NODE_ENV !== 'production' &&
      routeContentGroupCode &&
      programConfig?.contentGroupCode &&
      routeContentGroupCode !== programConfig.contentGroupCode
    ) {
      console.warn(
        '[TrainingArticleV3] contentGroupCode from routeContent does not match URL program config',
        {
          routeContentGroupCode,
          programConfigContentGroupCode: programConfig.contentGroupCode,
          pathname,
        },
      )
    }
  }, [routeContentGroupCode, programConfig, pathname])
  const instructorDataKey = programConfig?.instructorDataKey || null
  const courseNodeId = resolveCourseNodeId({ route, article })
  const coursePointer = routeContent?.coursePointer || null
  const siteId = currentSite?.siteId ?? null
  const trainingCourseCode = resolveTrainingCourseCodeForTree({
    programConfig,
    coursePointer,
    contentGroupCode,
    pathname,
  })
  const coursePointerForTreeFetch = buildCoursePointerForTreeFetch(
    coursePointer,
    trainingCourseCode,
  )
  const curriculumLmsCourseCode = parseCoursePointerQuery(coursePointer)
    .courseCode
  const curriculumLmsRequestKeysRef = useRef(new Set())

  useEffect(() => {
    // Course-scoped POST ensure is currently used for HS training/pathway
    // registrations (no classTitle). Lesson class creation still requires
    // classTitle via the create-class flow. K8 training continues to rely on
    // GET /instructor/me from withSiteContext.
    if (
      !useCurriculumLmsPayload ||
      !isHighSchool ||
      isPreviewRoute() ||
      !curriculumLmsCourseCode ||
      !contentGroupCode ||
      siteId == null
    ) {
      return
    }

    const requestKey = `${siteId}:${contentGroupCode}:${curriculumLmsCourseCode}`
    if (curriculumLmsRequestKeysRef.current.has(requestKey)) return
    curriculumLmsRequestKeysRef.current.add(requestKey)

    const request = dispatch(
      lmsOperations.createCourseInstanceByCourse({
        courseCode: curriculumLmsCourseCode,
        contentGroupCode,
        siteId,
      }),
    )

    request?.catch?.(() => {
      curriculumLmsRequestKeysRef.current.delete(requestKey)
    })
  }, [
    contentGroupCode,
    curriculumLmsCourseCode,
    dispatch,
    isHighSchool,
    siteId,
    useCurriculumLmsPayload,
  ])

  const courseTreeEntry = useSelector(
    curriculumCourseSelectors.selectCurriculumCourseTree(
      contentGroupCode,
      trainingCourseCode,
      siteId,
    ),
  )

  useEffect(() => {
    if (!coursePointerForTreeFetch || !contentGroupCode || siteId == null) {
      return
    }
    if (courseTreeEntry?.data || courseTreeEntry?.loading) return
    dispatch(
      curriculumCourseOperations.fetchCourseTreeFromPointer({
        coursePointer: coursePointerForTreeFetch,
        contentGroupCode,
        siteId,
      }),
    )
  }, [
    coursePointerForTreeFetch,
    contentGroupCode,
    siteId,
    courseTreeEntry?.data,
    courseTreeEntry?.loading,
    dispatch,
  ])

  const parsedEntry = mapArticleEntry(article)

  if (!parsedEntry) {
    return null
  }

  const isK5 = isK5Url()
  const isMs = isMiddleSchoolUrl()
  const numberOfCompletedTrainingPrograms = resolveTrainingProgress({
    isHighSchool,
    isK5,
    isMs,
    highSchoolInstructorData,
    instructorData,
    courseTreeData: courseTreeEntry?.data,
  })
  const markComplete = parsedEntry?.markComplete

  const shouldShowSiteSelector = showSiteSelector({
    checkMs: true,
    checkK5: true,
    checkHs: true,
  })

  return (
    <ResourcesContentV3
      {...parsedEntry}
      contentGroupCode={contentGroupCode}
      courseNodeId={courseNodeId}
      coursePointer={coursePointer}
      currentLocale={locale || parsedEntry.currentLocale}
      getEntryWithNewLocale={changeLocale}
      instructorDataKey={instructorDataKey}
      isHsPreview={isPreviewMode()}
      markComplete={markComplete}
      numberOfCompletedTrainingPrograms={numberOfCompletedTrainingPrograms}
      productTabTitle={productTabTitle}
      shouldShowSiteSelector={shouldShowSiteSelector}
      siteId={siteId}
      userContextManager={userContextManager}
    />
  )
}

TrainingArticleV3Container.propTypes = {
  currentSite: PropTypes.shape({
    siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  highSchoolInstructorData: PropTypes.object,
  instructorData: PropTypes.object,
  productTabTitle: PropTypes.string,
  routeContent: PropTypes.object,
  useCurriculumLmsPayload: PropTypes.bool,
  userContextManager: PropTypes.object,
}

const mapStateToProps = state => {
  const { productManager, userContextManager } = state

  return {
    highSchoolInstructorData: transformInstructorDataToOldShape(
      lmsSelectors.selectHsInstructorData(state),
    ),
    instructorData: getInstructorDataForCurrentProgram(state),
    productTabTitle: productManager?.productMap?.name || '',
    userContextManager,
  }
}

export const trainingArticleV3CurriculumOptions = {
  app: ({ route }) => resolveRouteServiceApp(route),
}

const ConnectedTrainingArticleV3Container = connect(mapStateToProps)(
  TrainingArticleV3Container,
)

const CurriculumTrainingArticleV3Container = props => (
  <ConnectedTrainingArticleV3Container
    {...props}
    useCurriculumLmsPayload={true}
  />
)

export const TrainingArticleV3 = withSiteContext(
  CurriculumRouteContentGetter(
    withApiError(CurriculumTrainingArticleV3Container),
    trainingArticleV3CurriculumOptions,
  ),
  // Avoid legacy HS POST /instructor/me with userContextB64; the container
  // ensures registrations via createCourseInstanceByCourse instead.
  { skipHsInstructorFetch: true },
)

export const PathwayTrainingArticleV3 = withSiteContext(
  CurriculumRouteContentGetter(
    withApiError(CurriculumTrainingArticleV3Container),
    trainingArticleV3CurriculumOptions,
  ),
  { skipHsInstructorFetch: true },
)

export default TrainingArticleV3
